// 레이드 「잿불 비룡」 VFX(RAID-PRD 9절, 레인 raid-vfx 2026-10-27). 게임 상태·이벤트만 읽는다(비룡 메시에 기대지 않는다 — 입 위치는
// BossVisual.wyvernMouth(pos, dir) — 비룡 GLB 입 뼈(#218) — 를 쓰고, 비룡이 없거나 숨었으면 보스 위치·향·높이로 추정).
//   브레스(공중 훑기): 경고가 찰 무렵 입 → 바닥 불줄기가 줄을 따라 훑고, 지나간 자리는 불길 띠 → 그을음 띠로 식는다. 열기 일렁임·불씨
//   화염 장판: 경고 원 위 조준 표식(조여드는 고리·차오르는 용암빛), 판정 0.3초 전 입에서 화염구 → 판정 순간 폭발(불꽃·불똥·연기·그을음·충격 고리)
//   착지 내려찍기: 내려오는 동안 흙바람 → 충격 고리 두 겹·흙먼지 고리·빛나는 균열·돌 조각·흙먼지 덩이·카메라 펀치(기존 CameraRig.punch, 8px 이하)
//   이륙: 날갯짓 흙먼지 소용돌이·흙먼지 고리·불씨 / 비행 중 아래 바람
//   물기·꼬리: 부채꼴 경고 가장자리 불길 / 판정 순간 이빨 섬광·불똥, 꼬리 호 참격·흙먼지
//   돌진: 내달리는 동안 발밑 흙먼지·불똥 줄
//   페이즈 2·광폭: 열기 터짐(고리·불씨), 그 뒤 보스 둘레 불씨·열기 증가(광폭은 붉은 바닥 맥동)
//   파티 직업 AI: raidVfxParty.ts(역할별 모듈)
//   하드(C13) 잿불 낙인: raidVfxBrand.ts(머리 위 룬·발밑 맥동 고리·기둥형 폭발). 하드 판은 보스 둘레 불씨·열기를 살짝 더 붉게
// 드로우콜 6(가산 입자·알파 입자·빛 바닥·그을음 바닥·돌 조각·표식). 섬광(화면 전체 밝아짐) 없음 — 밝은 것은 모두 그 자리 광휘다.
// 피 없음. quality(particleScale)가 상한·생성 수를 줄이고, 섬광 줄이기는 가산 밝기 60%·카메라 펀치 35%.
import * as THREE from 'three/webgpu';
import { Ev, TeleSource, type GameEvent } from '../shared/events';
import { MercEvent as MercEventKind } from '../game/raid';
import { MERCS, WYVERN, WyvernPattern } from '../shared/content/raid';
import { BossKind, BossState, TeleShape, TELEGRAPH_CAPACITY, type GameView } from '../shared/types';
import { AddKind, AlphaKind, Orient, RaidMarkers, RaidParticles } from './raidVfxParticles';
import { DarkKind, GlowKind, RaidGround } from './raidVfxGround';
import { RaidDebris } from './raidVfxDebris';
import { ProjKind, RaidProjectiles } from './raidVfxProjectiles';
import { BRAND_MARKERS, BRAND_PUNCH, BRAND_RADIUS, RaidBrandVfx } from './raidVfxBrand';
import { commonDodge, commonDown, commonRevive, createRoleVfx, PARTY_MARKER_END, roleOf, type PartyCtx, type RoleVfx } from './raidVfxParty';

/** 보스 몸(BossVisual): 부드럽게 따라간 위치와 공중 높이 */
export interface BossBody {
  readonly x: number; readonly z: number; readonly height: number;
  /** 비룡 입 뼈 위치·방향(월드, BossVisual #218). 비룡이 없거나 숨었으면 false */
  wyvernMouth?(pos: THREE.Vector3, dir?: THREE.Vector3): boolean;
  /** 비룡 몸에 붙은 임시 브레스 원뿔(BossVisual #218). 이 브레스가 대신 그리므로 끈다 */
  wyvernBreathCone?: boolean;
}

/** 표식 칸(파티 뒤): 광폭 열기 */
const MARK_ENRAGE = PARTY_MARKER_END;
/** 표식 칸: 하드 잿불 낙인(칸마다 룬 + 심 광휘) */
const MARK_BRAND = PARTY_MARKER_END + 2;
const MARK_CAP = MARK_BRAND + BRAND_MARKERS;

/** 브레스 훑기: 경고 끝 몇 틱 전부터 훑기 시작(비룡 불꽃 자세 s0와 맞춘다), 판정 뒤 꼬리 틱 */
export const BREATH_SWEEP = { lead: 34, tail: 12 } as const;
/** 화염구: 판정 몇 틱 전에 뱉는가(비룡 뱉기 자세 마지막 22%) */
export const SPIT_LEAD = 18;
/** 등장 착지 틱(wyvern 등장 자세 p 0.62) */
/** 레이드 섬 중심(RAID_MAPS.raid_wyvern 아레나 disk(0, -46, 19)) */
const ARENA_X = 0, ARENA_Z = -46;
const INTRO_LAND = Math.round(WYVERN.intro * 0.62);
/** 등장 포효(입이 가장 크게 열리는 틱): 착지 충격 뒤 포효 클립(actors/wyvern INTRO_DOWN + 21) 0.2초 */
const INTRO_ROAR = Math.round(WYVERN.intro * 0.5) + 21 + 12;

/** 하드 판 보스 둘레 불씨·열기 색(노말 EMBER보다 살짝 붉게) */
const EMBER_HARD = 0xff5a24, HEAT_HARD = 0xff4a24;
const EMBER = 0xff7a1a, EMBER_HOT = 0xffa83a, SMOKE = 0x2e2522, DUST = 0x8c7b6a, FIRE = 0xff5410;
/** 전체 가산 밝기(블룸에서 채도를 지키는 기준). 섬광 줄이기는 여기에 0.6을 곱한다 */
const BASE_BRIGHT = 0.68;

/** QA·테스트용 집계 */
export interface RaidVfxStats { add: number; alpha: number; glow: number; dark: number; debris: number; proj: number; mouthBone: number; mouthGuess: number; roars: number; breaths: number; fireballs: number; lands: number; bites: number; tails: number; heals: number; taunts: number; arrows: number; brands: number; brandBlasts: number; brandPulses: number; flashSkipped: number; drops: { add: number; alpha: number } }

export class RaidVfx {
  readonly group = new THREE.Group();
  readonly add: RaidParticles;
  readonly alpha: RaidParticles;
  readonly glow: RaidGround;
  readonly dark: RaidGround;
  readonly debris: RaidDebris;
  readonly marks = new RaidMarkers(MARK_CAP);
  readonly proj: RaidProjectiles;
  /** 하드 잿불 낙인(raidVfxBrand) */
  readonly brand: RaidBrandVfx;
  /** 카메라 펀치 요청(index.ts가 CameraRig.punch로 잇는다). px ≤ 8 */
  onPunch: ((px: number, ms: number, dx: number, dz: number) => void) | null = null;
  private readonly party: RoleVfx[];
  private readonly ctx: PartyCtx;
  private reduceFlash = false;
  /** 경고 칸 기억(발동 이벤트가 오면 칸이 이미 비었으므로 마지막으로 본 값을 쓴다) */
  private readonly tShape = new Int8Array(TELEGRAPH_CAPACITY).fill(-1);
  // 게임 경고 좌표는 double이라 Float64로 담는다(Float32면 3.1 같은 좌표가 매 프레임 '새 경고'로 잡혔다 — Codex P2 #217)
  private readonly tX = new Float64Array(TELEGRAPH_CAPACITY); private readonly tZ = new Float64Array(TELEGRAPH_CAPACITY);
  private readonly tR = new Float64Array(TELEGRAPH_CAPACITY); private readonly tA = new Float64Array(TELEGRAPH_CAPACITY);
  private readonly tW = new Float64Array(TELEGRAPH_CAPACITY); private readonly tTotal = new Float64Array(TELEGRAPH_CAPACITY);
  private readonly tPat = new Int8Array(TELEGRAPH_CAPACITY).fill(-1);
  private readonly tWas = new Uint8Array(TELEGRAPH_CAPACITY);
  private readonly tSpit = new Uint8Array(TELEGRAPH_CAPACITY);
  /** 브레스 훑기 경과(초, -1 = 없음) */
  private readonly tSweep = new Float32Array(TELEGRAPH_CAPACITY).fill(-1);
  private readonly tDecal = new Int32Array(TELEGRAPH_CAPACITY).fill(-1);
  private wasActive = false;
  private lastStateTick = 0;
  private lastState = -1;
  private chargeLeft = 0;
  private yaw = 0;
  private hasYaw = false;
  private enrageId = -1;
  /** 연속 생성 누적(초당 n개를 프레임과 무관하게) */
  private accA = 0; private accB = 0; private accC = 0; private accD = 0; private accAir = 0;
  private readonly mouth = new THREE.Vector3();
  /** 마지막 프레임의 보스 몸(이벤트 처리에서 입 위치를 찾는다) */
  private lastBody: BossBody | null = null;
  private readonly mouthDir = new THREE.Vector3();
  /** QA: 입 뼈로 불을 낸 횟수(추정 위치를 쓴 횟수와 구별) */
  mouthBone = 0;
  mouthGuess = 0;
  private readonly tmp = new THREE.Vector3();
  private readonly cnt = { roars: 0, breaths: 0, fireballs: 0, lands: 0, bites: 0, tails: 0, heals: 0, taunts: 0, arrows: 0 };

  constructor(particleScale: number) {
    this.group.name = 'raid-vfx';
    this.add = new RaidParticles('add', particleScale, 'fx-raid-add');
    this.alpha = new RaidParticles('alpha', particleScale, 'fx-raid-smoke');
    this.glow = new RaidGround('glow', particleScale, 'fx-raid-glow');
    this.dark = new RaidGround('dark', particleScale, 'fx-raid-scorch');
    this.debris = new RaidDebris(particleScale);
    this.proj = new RaidProjectiles(this.add, this.glow);
    this.brand = new RaidBrandVfx(this, MARK_BRAND);
    this.group.add(this.dark.mesh, this.glow.mesh, this.debris.mesh, this.alpha.mesh, this.add.mesh, this.marks.mesh);
    this.party = MERCS.map((m) => createRoleVfx(roleOf(m.role)));
    this.ctx = {
      add: this.add, glow: this.glow, marks: this.marks, proj: this.proj,
      bossX: 0, bossZ: 0, bossR: WYVERN.radius, bossMidY: 2.6, bossTopY: 6,
      slotX: new Float32Array(1 + MERCS.length), slotZ: new Float32Array(1 + MERCS.length), time: 0, tauntTicks: 0,
    };
    this.setReduceFlash(false);
  }

  /** 품질 단계가 바뀌면 생성 수만 줄인다(상한은 부팅 품질로 고정 — 실행 중 버퍼 재할당 없음) */
  setScale(particleScale: number): void {
    const s = Math.max(0.25, Math.min(1, particleScale));
    this.add.scale = s; this.alpha.scale = s; this.debris.scale = s;
  }

  setReduceFlash(on: boolean): void {
    this.reduceFlash = on;
    const d = (on ? 0.6 : 1) * BASE_BRIGHT;
    this.add.dim = d; this.glow.dim = d; this.marks.dim = d;
  }

  get stats(): RaidVfxStats {
    return { add: this.add.active, alpha: this.alpha.active, glow: this.glow.active, dark: this.dark.active, debris: this.debris.active, proj: this.proj.active, mouthBone: this.mouthBone, mouthGuess: this.mouthGuess, ...this.cnt, brands: this.brand.brands, brandBlasts: this.brand.blasts, brandPulses: this.brand.pulses, flashSkipped: this.brand.flashSkipped, drops: { add: this.add.dropped, alpha: this.alpha.dropped } };
  }

  private punch(px: number, ms: number, x: number, z: number, view: GameView): void {
    if (!this.onPunch) return;
    const h = view.hero;
    this.onPunch(px * (this.reduceFlash ? 0.35 : 1), ms, h.x - x, h.z - z);
  }

  // ── 이벤트 ───────────────────────────────────────
  onEvent(e: GameEvent, view: GameView): void {
    if (e.type === Ev.SceneChange) { this.clear(); return; }
    const r = view.raid;
    if (!r || !r.active) return;
    switch (e.type) {
      case Ev.TelegraphFire:
        if (e.c === TeleSource.Boss && e.d === BossKind.Wyvern) this.strike(e.a | 0, view);
        break;
      case Ev.BossPattern:
        if (e.b !== BossKind.Wyvern) break;
        if (e.a === WyvernPattern.phase2) { this.heatBurst(e.x, e.z, 0xffa040, false, view); this.roarBlast(0.85, view); }
        else if (e.a === WyvernPattern.enrage) this.heatBurst(e.x, e.z, 0xff3a20, true, view);
        else if (RaidBrandVfx.isBlast(e.a, e.c)) { if (this.brand.explode(e.x, e.z, this.ctx.time)) this.punch(BRAND_PUNCH.px, BRAND_PUNCH.ms, e.x, e.z, view); }
        break;
      case Ev.RaidMerc: this.mercEvent(e, view); break;
      case Ev.BossDefeated:
        if (e.b === BossKind.Wyvern) this.defeated(e.x, e.z);
        break;
    }
  }

  private mercEvent(e: GameEvent, view: GameView): void {
    const i = e.a | 0, slot = i + 1, mod = this.party[i];
    if (!mod) return;
    this.syncSlots(view, 1);
    const x = this.ctx.slotX[slot], z = this.ctx.slotZ[slot];
    switch (e.b) {
      case MercEventKind.Attack:
        if (e.c > 0) { mod.onAttack(this.ctx, slot); if (mod.role === 'archer') this.cnt.arrows++; }
        break;
      case MercEventKind.Heal: mod.onSkill(this.ctx, slot, e.c); this.cnt.heals++; break;
      case MercEventKind.Taunt: mod.onSkill(this.ctx, slot, e.c); this.cnt.taunts++; break;
      case MercEventKind.Down: commonDown(this.ctx, this.alpha, x, z); break;
      case MercEventKind.Revive: commonRevive(this.ctx, x, z); break;
      case MercEventKind.Dodge: commonDodge(this.alpha, x, z); break;
    }
  }

  /** 경고 판정 순간(칸 기억으로 패턴을 안다) */
  private strike(slot: number, view: GameView): void {
    if (slot < 0 || slot >= TELEGRAPH_CAPACITY || this.tShape[slot] < 0) return;
    const pat = this.tPat[slot], x = this.tX[slot], z = this.tZ[slot];
    switch (pat) {
      case WyvernPattern.bite: this.biteFx(slot); break;
      case WyvernPattern.tail: this.tailFx(slot); break;
      case WyvernPattern.pools: this.fireballBlast(x, z, view); break;
      case WyvernPattern.charge: this.chargeLeft = WYVERN.charge.dashTicks / 60 + 0.25; this.dustBurst(x, z, 1.5, 6, 2.2); break;
      case WyvernPattern.breath: this.breathStrike(slot, view); break;
      case WyvernPattern.land: this.landImpact(x, z, 1, view); break;
    }
    // 브레스 불줄기는 판정 뒤 꼬리 몇 틱을 더 그린다(tSweep은 scanTelegraphs가 끝낸다)
    this.tShape[slot] = -1;
  }

  // ── 매 프레임 ─────────────────────────────────────
  /** dt = 세계 시계 초(일시정지·히트스톱 0), time = 세계 시계 초(누적) */
  update(view: GameView, alpha: number, dt: number, camera: THREE.Camera, time: number, body: BossBody | null): void {
    const r = view.raid;
    const active = !!r && r.active;
    if (!active) {
      if (this.wasActive) this.clear();
      this.wasActive = false;
      this.group.visible = false;
      return;
    }
    this.wasActive = true;
    this.group.visible = true;
    const b = view.boss, ctx = this.ctx;
    ctx.time = time;
    ctx.tauntTicks = r!.tauntTicks;
    // 보스 몸: 렌더가 부드럽게 따라간 위치(공중에서 게임 위치가 뛰어도 몸을 따라간다)
    const bx = body ? body.x : b.prevX + (b.x - b.prevX) * alpha, bz = body ? body.z : b.prevZ + (b.z - b.prevZ) * alpha;
    const bh = body ? body.height : (b.air ?? 0) * (WYVERN.flight.height + 1.5);
    ctx.bossX = bx; ctx.bossZ = bz; ctx.bossMidY = bh + 2.8; ctx.bossTopY = bh + 5.4;
    if (!this.hasYaw) { this.yaw = b.facing; this.hasYaw = true; }
    this.yaw += angleDelta(this.yaw, b.facing) * Math.min(1, dt * ((b.air ?? 0) > 0.02 ? 3 : 14));
    this.syncSlots(view, alpha);
    this.hideBreathCone(body);
    this.lastBody = body;
    this.marks.begin(camera, time);
    const fighting = b.state !== BossState.Inactive && b.state !== BossState.Dead;
    if (fighting) {
      this.scanTelegraphs(view, dt, body);
      this.statePolls(view, dt, body);
      this.ambient(view, dt);
    }
    for (let i = 0; i < this.party.length; i++) this.party[i].update(ctx, dt, i + 1);
    this.brandTrack(view, fighting ? dt : 0, time);
    this.proj.update(dt);
    this.add.update(dt, camera, time);
    this.alpha.update(dt, camera, time);
    this.glow.update(dt, time);
    this.dark.update(dt, time);
    this.debris.update(dt);
    this.marks.end();
  }

  /**
   * 하드 잿불 낙인: 살아 있는 낙인 경고(텔레그래프)를 매 프레임 따라간다 — 원마다 덧붙이고, 사라진 원은 거둔다(솔로·4인 넷 판 같은 길).
   * 낙인 경고 = 생길 때 패턴이 brand였던 칸, 또는(넷 판에서 패턴 값이 늦게 와도) 낙인이 붙어 있는 동안의 낙인 반경 원
   */
  private brandTrack(view: GameView, dt: number, time: number): void {
    const B = this.brand, tel = view.telegraphs, r = view.raid!, ctx = this.ctx;
    B.begin();
    const branding = r.pattern === WyvernPattern.brand || ((r.brandSlots ?? 0) | 0) !== 0;
    for (let i = 0; i < tel.length && i < TELEGRAPH_CAPACITY; i++) {
      const t = tel[i];
      if (!t.active || t.shape !== TeleShape.Circle) continue;
      if (this.tPat[i] !== WyvernPattern.brand && !(branding && Math.abs(t.radius - BRAND_RADIUS) < 0.05)) continue;
      B.track(i, t.x, t.z, Math.max(0, t.total - t.tick), t.total, ctx.slotX, ctx.slotZ, dt, time);
    }
    B.end();
  }

  private syncSlots(view: GameView, alpha: number): void {
    const h = view.hero, ctx = this.ctx;
    ctx.slotX[0] = h.prevX + (h.x - h.prevX) * alpha; ctx.slotZ[0] = h.prevZ + (h.z - h.prevZ) * alpha;
    const ms = view.raid ? view.raid.mercs : null;
    if (!ms) return;
    for (let i = 0; i < ms.length && i + 1 < ctx.slotX.length; i++) {
      const m = ms[i];
      ctx.slotX[i + 1] = m.prevX + (m.x - m.prevX) * alpha; ctx.slotZ[i + 1] = m.prevZ + (m.z - m.prevZ) * alpha;
    }
  }

  /** 비룡 몸의 임시 브레스 원뿔을 끈다(이 브레스가 대신 그린다 — BossVisual.wyvernBreathCone, #218) */
  private hideBreathCone(body: BossBody | null): void {
    if (body && body.wyvernBreathCone === true) body.wyvernBreathCone = false;
  }

  /** 입 위치·방향(세계). 비룡 입 뼈(BossVisual.wyvernMouth) → 없으면 보스 위치·향·높이로 추정. 입 뼈를 썼으면 true */
  mouthWorld(body: BossBody | null, out: THREE.Vector3, dir: THREE.Vector3 = this.mouthDir): boolean {
    if (body && typeof body.wyvernMouth === 'function' && body.wyvernMouth(out, dir)) {
      if (!(dir.lengthSq() > 1e-6)) dir.set(Math.sin(this.yaw), -0.5, Math.cos(this.yaw)).normalize();
      return true;
    }
    const c = this.ctx;
    out.set(c.bossX + Math.sin(this.yaw) * 5.2, c.bossMidY + 0.4, c.bossZ + Math.cos(this.yaw) * 5.2);
    dir.set(Math.sin(this.yaw), -0.5, Math.cos(this.yaw)).normalize();
    return false;
  }

  /** 활성 경고 칸을 기억하고, 패턴별 진행 연출(조준 표식·화염구 발사·브레스 훑기·부채꼴 가장자리)을 한다 */
  private scanTelegraphs(view: GameView, dt: number, body: BossBody | null): void {
    const tel = view.telegraphs, pat = view.raid!.pattern;
    for (let i = 0; i < tel.length && i < TELEGRAPH_CAPACITY; i++) {
      const t = tel[i];
      if (!t.active) {
        // 경고가 막 사라졌다(판정 또는 경직 취소): 수명 정리는 endTelegraph 한 곳에서
        if (this.tWas[i]) this.endTelegraph(i);
        this.tWas[i] = 0;
        continue;
      }
      // 잿불 낙인 원은 주인을 따라 움직인다 — 좌표가 바뀌어도 새 경고가 아니다(연출은 raidVfxBrand가 상태로 그린다)
      const follow = this.tWas[i] === 1 && this.tPat[i] === WyvernPattern.brand && this.tShape[i] === t.shape;
      const born = !follow && (this.tWas[i] === 0 || this.tShape[i] !== t.shape || this.tX[i] !== t.x || this.tZ[i] !== t.z);
      this.tWas[i] = 1;
      if (born) {
        if (this.tDecal[i] >= 0) { this.glow.end(this.tDecal[i], 0.05); this.tDecal[i] = -1; }
        this.tShape[i] = t.shape; this.tX[i] = t.x; this.tZ[i] = t.z; this.tR[i] = t.radius; this.tA[i] = t.angle; this.tW[i] = t.width; this.tTotal[i] = t.total;
        this.tPat[i] = pat; this.tSpit[i] = 0; this.tSweep[i] = -1;
        this.telegraphBorn(i, t.tick);
      }
      this.tA[i] = t.angle; this.tR[i] = t.radius;
      const left = t.total - t.tick;
      const p = this.tPat[i];
      if (p === WyvernPattern.pools && !this.tSpit[i] && left <= SPIT_LEAD) {
        this.tSpit[i] = 1;
        if (this.mouthWorld(body, this.mouth)) this.mouthBone++; else this.mouthGuess++;
        this.proj.fire(ProjKind.Fireball, this.mouth.x, this.mouth.y, this.mouth.z, t.x, 0.35, t.z, Math.max(0.1, left / 60), 2.2 + this.add.range(0, 1.5), FIRE);
        this.cnt.fireballs++;
      }
      if (p === WyvernPattern.pools || p === WyvernPattern.land) this.poolWindup(i, t.tick / Math.max(1, t.total), dt);
      if (p === WyvernPattern.breath) {
        if (this.tSweep[i] < 0 && left <= BREATH_SWEEP.lead) { this.tSweep[i] = 0; this.cnt.breaths++; }
        if (this.tSweep[i] >= 0) this.breathSweep(i, dt, body);
        else this.breathWindup(i, dt);
      }
      if (p === WyvernPattern.tail && dt > 0) this.tailWindup(i, dt);
    }
    // 판정이 지나도 꼬리 몇 틱은 불줄기가 남는다(칸은 이미 비었다)
    for (let i = 0; i < TELEGRAPH_CAPACITY; i++) {
      if (tel[i]?.active || this.tSweep[i] < 0) continue;
      this.breathSweep(i, dt, body);
      if (this.tSweep[i] > (BREATH_SWEEP.lead + BREATH_SWEEP.tail) / 60) this.tSweep[i] = -1;
    }
  }

  /**
   * 경고 수명 정리(한 곳): 칸이 비면 그 칸에 붙은 데칼을 바로 끝낸다. 판정 없이 비었으면(경직으로 패턴 취소 — Codex P2 #217)
   * 브레스 훑기도 멈추고 칸 기억을 지운다. 판정된 칸은 strike()가 tShape를 먼저 지우므로 훑기 꼬리만 남는다.
   */
  private endTelegraph(i: number): void {
    if (this.tDecal[i] >= 0) { this.glow.end(this.tDecal[i], 0.08); this.tDecal[i] = -1; }
    if (this.tShape[i] >= 0) { this.tShape[i] = -1; this.tSweep[i] = -1; this.cancelled++; }
  }
  /** QA: 판정 없이 사라진 경고 수 */
  cancelled = 0;

  /** 경고가 막 생겼다: 바닥 조준 표식·부채꼴 가장자리(경고 남은 시간만큼) */
  private telegraphBorn(i: number, tick: number): void {
    const p = this.tPat[i], life = Math.max(0.1, (this.tTotal[i] - tick) / 60);
    this.tDecal[i] = -1;
    if (p === WyvernPattern.pools || p === WyvernPattern.land) {
      const rr = this.tR[i];
      this.tDecal[i] = this.glow.add(GlowKind.Target, this.tX[i], this.tZ[i], 0, rr * 1.05, rr * 1.05, rr * 1.05, rr * 1.05, life, p === WyvernPattern.land ? 0xff5a2a : 0xff7a2a, 1.5, 0.95);
    } else if (p === WyvernPattern.bite || p === WyvernPattern.tail) {
      const rr = this.tR[i];
      this.tDecal[i] = this.glow.add(GlowKind.ConeEdge, this.tX[i], this.tZ[i], this.tA[i], rr, rr, rr, rr, life, 0xff6a2a, 1.4, 0.8, this.tW[i] / 2);
    }
  }

  /** 화염 장판·착지 경고: 원 가장자리에서 가운데로 모이는 불씨(진행할수록 빨리) */
  private poolWindup(i: number, prog: number, dt: number): void {
    if (dt <= 0) return;
    const P = this.add, rr = this.tR[i], x = this.tX[i], z = this.tZ[i];
    this.accA += dt * (6 + 22 * prog) * P.scale;
    while (this.accA >= 1) {
      this.accA -= 1;
      const a = P.range(0, Math.PI * 2), s = rr * P.range(0.85, 1.0);
      const px = x + Math.sin(a) * s, pz = z + Math.cos(a) * s;
      P.emit(AddKind.Ember, Orient.Camera, px, 0.15, pz, -Math.sin(a) * s * 1.1, P.range(0.4, 1.4), -Math.cos(a) * s * 1.1, 0.7, 0.16, 0.05, prog > 0.6 ? EMBER_HOT : EMBER, 1.9, 0.9, 0, 0.3);
    }
  }

  /** 꼬리 준비: 몸 뒤쪽 바닥에서 흙먼지가 꼬리 쪽으로 쓸린다 */
  private tailWindup(i: number, dt: number): void {
    const A = this.alpha;
    this.accD += dt * 5 * A.scale;
    while (this.accD >= 1) {
      this.accD -= 1;
      const a = this.tA[i] + A.range(-0.6, 0.6), d = this.tR[i] * A.range(0.4, 0.8);
      A.emit(AlphaKind.Dust, Orient.Camera, this.tX[i] + Math.sin(a) * d, 0.3, this.tZ[i] + Math.cos(a) * d, Math.cos(a) * 1.5, A.range(0.2, 0.6), -Math.sin(a) * 1.5, 0.8, 0.6, 1.6, DUST, 1, 0.4, 0, 1.8);
    }
  }

  /** 브레스 줄 경고 동안: 줄 위에서 불씨가 하나둘 솟는다(곧 탄다는 예고) */
  private breathWindup(i: number, dt: number): void {
    if (dt <= 0) return;
    const P = this.add, L = this.tR[i], W = this.tW[i], a = this.tA[i];
    this.accB += dt * 10 * P.scale;
    while (this.accB >= 1) {
      this.accB -= 1;
      const u = P.rnd() * L, v = P.range(-0.4, 0.4) * W;
      const x = this.tX[i] + Math.sin(a) * u + Math.cos(a) * v, z = this.tZ[i] + Math.cos(a) * u - Math.sin(a) * v;
      P.emit(AddKind.Ember, Orient.Camera, x, 0.1, z, P.range(-0.3, 0.3), P.range(1.2, 2.6), P.range(-0.3, 0.3), 0.9, 0.16, 0.04, EMBER, 1.8, 0.8, 0.4, 0.4);
    }
  }

  /** 브레스 훑기: 입 → 바닥 불줄기 + 바닥에 닿는 자리 불길·불똥·연기·열기 */
  private breathSweep(i: number, dt: number, body: BossBody | null): void {
    if (dt <= 0) return;
    this.tSweep[i] += dt;
    const P = this.add, A = this.alpha;
    const k = Math.min(1, this.tSweep[i] / (BREATH_SWEEP.lead / 60));
    const fading = this.tSweep[i] > BREATH_SWEEP.lead / 60 ? 1 - (this.tSweep[i] - BREATH_SWEEP.lead / 60) / (BREATH_SWEEP.tail / 60) : 1;
    if (fading <= 0) return;
    const L = this.tR[i], a = this.tA[i], sa = Math.sin(a), ca = Math.cos(a);
    // 바닥에 닿는 점: 줄 시작 → 끝(살짝 앞서 나간다)
    const u = L * (0.04 + 0.92 * easeInOut(k));
    const gx = this.tX[i] + sa * u, gz = this.tZ[i] + ca * u;
    if (this.mouthWorld(body, this.mouth)) this.mouthBone++; else this.mouthGuess++;
    const mx = this.mouth.x, my = this.mouth.y, mz = this.mouth.z;
    const dx = gx - mx, dy = 0.4 - my, dz = gz - mz, dl = Math.hypot(dx, dy, dz) || 1;
    const md = this.mouthDir;
    // 불줄기
    this.accC += dt * 90 * P.scale * fading;
    while (this.accC >= 1) {
      this.accC -= 1;
      const f = Math.pow(P.rnd(), 0.8), sp = P.range(10, 16);
      const jx = P.range(-0.5, 0.5) * f, jy = P.range(-0.3, 0.3) * f, jz = P.range(-0.5, 0.5) * f;
      // 입 앞 25%는 입 뼈 방향으로 뿜어 나가다 바닥 점 쪽으로 휜다(입에서 불이 나오는 것처럼)
      const w = Math.min(1, f / 0.25);
      const ux = md.x * (1 - w) + dx / dl * w, uy = md.y * (1 - w) + dy / dl * w, uz = md.z * (1 - w) + dz / dl * w;
      const bend = (1 - w) * f * dl * 0.5;
      P.emit(AddKind.Flame, Orient.Camera, mx + dx * f + md.x * bend + jx, my + dy * f + md.y * bend + jy, mz + dz * f + md.z * bend + jz, ux * sp, uy * sp, uz * sp,
        P.range(0.12, 0.2), 0.35 + 1.0 * f, 0.9 + 2.0 * f, f < 0.25 ? EMBER_HOT : FIRE, 2.2, 0.85, 0, 2);
    }
    // 바닥 불길(솟는 불꽃 덩이) + 불똥 + 연기
    this.accD += dt * 50 * P.scale * fading;
    while (this.accD >= 1) {
      this.accD -= 1;
      const v = P.range(-2.6, 2.6), along = P.range(-1.5, 0.5);
      const x = gx + ca * v + sa * along, z = gz - sa * v + ca * along;
      P.emit(AddKind.Flame, Orient.Camera, x, 0.4, z, sa * P.range(1, 4) + P.range(-1, 1), P.range(2, 5), ca * P.range(1, 4) + P.range(-1, 1), P.range(0.35, 0.6), P.range(1.2, 1.8), P.range(2.4, 3.4), P.rnd() < 0.3 ? EMBER_HOT : FIRE, 2.0, 0.85, 2, 2.4);
      if (P.rnd() < 0.6) P.emit(AddKind.Streak, Orient.Velocity, x, 0.3, z, P.range(-5, 5) + sa * 4, P.range(3, 9), P.range(-5, 5) + ca * 4, P.range(0.3, 0.6), 0.07, 0.03, EMBER_HOT, 2.2, 0.95, -12, 1, 0.05);
      if (A.rnd() < 0.25) A.emit(AlphaKind.Smoke, Orient.Camera, x, 1.5, z, A.range(-0.5, 0.5), A.range(1.5, 2.8), A.range(-0.5, 0.5), A.range(1.2, 1.8), 1.4, 3.6, SMOKE, 1, 0.5, 0.2, 0.8);
    }
    // 입 앞 광휘
    this.marks.set(MARK_ENRAGE + 1, AddKind.Flare, Orient.Camera, mx, my, mz, 2.4, 1, EMBER_HOT, 2.2, 0.9 * fading);
  }

  /** 브레스 판정: 줄 전체 불길 띠(식으며 붉게) + 그을음 띠 + 줄 따라 불씨·불꽃 + 끝 열 원판 + 카메라 */
  private breathStrike(i: number, view: GameView): void {
    const P = this.add, L = this.tR[i], W = this.tW[i], a = this.tA[i], sa = Math.sin(a), ca = Math.cos(a);
    const cx = this.tX[i] + sa * L / 2, cz = this.tZ[i] + ca * L / 2;
    this.glow.add(GlowKind.BurnStrip, cx, cz, a, W * 0.5, W * 0.55, L / 2, L / 2, 2.6, FIRE, 1.8, 0.95);
    this.dark.add(DarkKind.ScorchStrip, cx, cz, a, W * 0.5, W * 0.55, L / 2, L / 2, 9, 0x140c0a, 1, 0.75);
    for (let c = 0; c < P.count(46, 12); c++) {
      const u = P.rnd() * L, v = P.range(-0.45, 0.45) * W;
      const x = this.tX[i] + sa * u + ca * v, z = this.tZ[i] + ca * u - sa * v;
      if (c % 3 === 0) P.emit(AddKind.Flame, Orient.Camera, x, 0.5, z, 0, P.range(2, 4), 0, P.range(0.4, 0.8), 1.2, 2.8, FIRE, 1.9, 0.8, 1.5, 2);
      else P.emit(AddKind.Ember, Orient.Camera, x, 0.2, z, P.range(-0.6, 0.6), P.range(1.5, 4.5), P.range(-0.6, 0.6), P.range(0.9, 1.8), 0.16, 0.04, c % 2 ? EMBER : EMBER_HOT, 1.9, 0.9, 0.3, 0.5);
    }
    for (let c = 0; c < P.count(6, 2); c++) {
      const u = P.rnd() * L;
      P.emit(AddKind.Heat, Orient.Upright, this.tX[i] + sa * u, 1.6, this.tZ[i] + ca * u, 0, 0.4, 0, P.range(1.2, 2), 1.4, 1.8, 0xff9a50, 0.6, 0.35, 0, 0, 0, 1.6);
    }
    this.punch(4, 140, cx, cz, view);
  }

  /** 화염구 착탄 = 화염 장판 판정 */
  private fireballBlast(x: number, z: number, view: GameView): void {
    const P = this.add, A = this.alpha, rr = WYVERN.pools.radius;
    // 광휘는 판정 원(지름 ≈ 2·반지름)보다 크게 번지지 않게(L11 비평: 흰 광구가 파티를 덮었다)
    P.emit(AddKind.Flare, Orient.Camera, x, 1.0, z, 0, 0, 0, 0.24, 1.0, rr * 1.2, EMBER_HOT, 1.2, 0.75);
    for (let c = 0; c < P.count(9, 4); c++) {
      const a = P.range(0, Math.PI * 2), s = P.range(1.5, 4.5);
      P.emit(AddKind.Flame, Orient.Camera, x + Math.sin(a) * 0.5, 0.6, z + Math.cos(a) * 0.5, Math.sin(a) * s, P.range(2.5, 6), Math.cos(a) * s, P.range(0.35, 0.65), P.range(0.9, 1.3), P.range(1.8, 2.6), c % 3 ? FIRE : EMBER_HOT, 1.35, 0.7, 1, 3);
    }
    for (let c = 0; c < P.count(16, 6); c++) {
      const a = P.range(0, Math.PI * 2), s = P.range(6, 13);
      P.emit(AddKind.Streak, Orient.Velocity, x, 0.5, z, Math.sin(a) * s, P.range(4, 10), Math.cos(a) * s, P.range(0.35, 0.7), 0.08, 0.03, c % 2 ? EMBER_HOT : 0xffffff, 2.3, 1, -15, 1, 0.05);
    }
    for (let c = 0; c < A.count(5, 2); c++) {
      A.emit(AlphaKind.Smoke, Orient.Camera, x + A.range(-1, 1), 1.6, z + A.range(-1, 1), A.range(-0.6, 0.6), A.range(1.5, 3), A.range(-0.6, 0.6), A.range(1.4, 2.2), 1.6, 3.8, SMOKE, 1, 0.55, 0.2, 0.7);
    }
    this.glow.add(GlowKind.Shock, x, z, 0, 0.5, rr * 1.6, 0.5, rr * 1.6, 0.4, EMBER_HOT, 1.3, 0.8);
    this.glow.add(GlowKind.Disc, x, z, 0, rr * 0.9, rr * 1.05, rr * 0.9, rr * 1.05, 0.4, FIRE, 1.1, 0.6);
    this.dark.add(DarkKind.Scorch, x, z, this.add.range(0, 6.28), rr * 1.15, rr * 1.2, rr * 1.15, rr * 1.2, WYVERN.fire.ticks / 60 + 3, 0x120a08, 1, 0.7);
    this.punch(2.5, 100, x, z, view);
  }

  /** 착지 내려찍기(scale 1) · 등장 착지(scale 0.65) */
  private landImpact(x: number, z: number, scale: number, view: GameView): void {
    const P = this.add, rr = WYVERN.flight.landRadius * scale;
    this.cnt.lands++;
    this.glow.add(GlowKind.Shock, x, z, 0, 1, rr * 2.2, 1, rr * 2.2, 0.5, 0xffd8a0, 1.25, 0.7);
    this.glow.add(GlowKind.Shock, x, z, 0, 1, rr * 1.6, 1, rr * 1.6, 0.75, FIRE, 1.9, 0.95);
    this.glow.add(GlowKind.Cracks, x, z, this.add.range(0, 6.28), rr * 1.1, rr * 1.15, rr * 1.1, rr * 1.15, 2.2, FIRE, 2.0, 0.95);
    this.dark.add(DarkKind.Cracks, x, z, this.add.range(0, 6.28), rr * 1.15, rr * 1.2, rr * 1.15, rr * 1.2, 6, 0x0e0806, 1, 0.8);
    this.dark.add(DarkKind.DustRing, x, z, 0, rr * 0.6, rr * 2.4, rr * 0.6, rr * 2.4, 1.3, 0x6e604f, 1, 0.75);
    this.debris.burst(x, z, rr * 0.7, Math.round(24 * scale), 7 * scale, 9 * scale, 0.35, 0.9);
    this.dustBurst(x, z, rr * 0.8, Math.round(18 * scale), 5 * scale);
    P.emit(AddKind.Flare, Orient.Camera, x, 1.0, z, 0, 0, 0, 0.22, 2, 4.5 * scale, EMBER_HOT, 1.7, 0.8);
    for (let c = 0; c < P.count(Math.round(22 * scale), 6); c++) {
      const a = P.range(0, Math.PI * 2), s = P.range(7, 15);
      P.emit(AddKind.Streak, Orient.Velocity, x + Math.sin(a) * rr * 0.4, 0.4, z + Math.cos(a) * rr * 0.4, Math.sin(a) * s, P.range(2, 7), Math.cos(a) * s, P.range(0.3, 0.6), 0.09, 0.03, c % 2 ? EMBER_HOT : 0xfff0d0, 2.3, 1, -14, 1, 0.05);
    }
    this.punch(8 * scale, 160, x, z, view);
  }

  /** 바깥으로 구르는 흙먼지 덩이(알파) */
  private dustBurst(x: number, z: number, r: number, n: number, speed: number): void {
    const A = this.alpha;
    for (let c = 0; c < A.count(n, 3); c++) {
      const a = (c / n) * Math.PI * 2 + A.range(-0.2, 0.2), s = speed * A.range(0.6, 1.1);
      A.emit(AlphaKind.Dust, Orient.Camera, x + Math.sin(a) * r, 0.5, z + Math.cos(a) * r, Math.sin(a) * s, A.range(0.4, 1.4), Math.cos(a) * s, A.range(0.9, 1.5), A.range(1.0, 1.4), A.range(2.6, 3.6), DUST, 1, 0.62, 0.3, 1.8);
    }
  }

  private biteFx(slot: number): void {
    const P = this.add, a = this.tA[slot], rr = this.tR[slot] * 0.55;
    const x = this.tX[slot] + Math.sin(a) * rr, z = this.tZ[slot] + Math.cos(a) * rr;
    this.cnt.bites++;
    // 이빨 섬광: 위·아래에서 마주 닫히는 굵은 줄 두 개 + 주황 불똥 + 작은 충격 고리
    const tx = Math.cos(a), tz = -Math.sin(a);
    for (const up of [1, -1]) {
      P.emit(AddKind.Streak, Orient.Velocity, x - tx * 1.6, 1.4 + up * 1.0, z - tz * 1.6, tx * 22, -up * 7, tz * 22, 0.13, 0.28, 0.14, up > 0 ? 0xffffff : 0xffb070, 2.4, 1, 0, 8, 0.09);
    }
    P.emit(AddKind.Flare, Orient.Camera, x, 1.3, z, 0, 0, 0, 0.18, 1.2, 3.0, 0xffc890, 2.2, 0.9);
    for (let c = 0; c < P.count(12, 4); c++) {
      const s = P.range(5, 11), aa = a + P.range(-0.9, 0.9);
      P.emit(AddKind.Streak, Orient.Velocity, x, 1.2, z, Math.sin(aa) * s, P.range(1, 6), Math.cos(aa) * s, P.range(0.2, 0.4), 0.08, 0.03, c % 2 ? 0xffb060 : 0xffffff, 2.3, 1, -14, 2, 0.05);
    }
    this.glow.add(GlowKind.Shock, x, z, 0, 0.5, 3.2, 0.5, 3.2, 0.35, 0xffb070, 1.5, 0.8);
    this.dustBurst(x, z, 0.8, 6, 3);
  }

  private tailFx(slot: number): void {
    const P = this.add, A = this.alpha, a0 = this.tA[slot], half = this.tW[slot] / 2, rr = this.tR[slot] * 0.72;
    const x0 = this.tX[slot], z0 = this.tZ[slot];
    this.cnt.tails++;
    // 꼬리 호: 호를 따라 접선 방향으로 미끄러지는 참격 줄 + 바닥 흙먼지 + 불똥
    const n = P.count(14, 7);
    for (let c = 0; c < n; c++) {
      const f = c / (n - 1), a = a0 - half + 2 * half * f;
      const x = x0 + Math.sin(a) * rr, z = z0 + Math.cos(a) * rr;
      const tx = Math.cos(a), tz = -Math.sin(a), s = 20;
      P.emit(AddKind.Streak, Orient.Velocity, x, 0.8, z, tx * s, 0, tz * s, 0.22, 0.3, 0.12, c % 2 ? 0xffb070 : 0xfff0d8, 2.0, 0.95, 0, 4, 0.12);
      if (c % 2 === 0) A.emit(AlphaKind.Dust, Orient.Camera, x, 0.4, z, tx * 4 + Math.sin(a) * 2, A.range(0.4, 1), tz * 4 + Math.cos(a) * 2, A.range(0.8, 1.2), 1.0, 2.8, DUST, 1, 0.55, 0.2, 2);
      if (c % 3 === 0) P.emit(AddKind.Streak, Orient.Velocity, x, 0.5, z, Math.sin(a) * 7, P.range(2, 5), Math.cos(a) * 7, 0.35, 0.07, 0.03, 0xffb060, 2.2, 0.9, -12, 1, 0.05);
    }
    this.dark.add(DarkKind.DustRing, x0, z0, 0, rr * 0.8, rr * 1.4, rr * 0.8, rr * 1.4, 0.9, 0x6e604f, 1, 0.5);
  }

  /** 페이즈 2(주황)·광폭(붉게): 열기 고리 + 위로 솟는 불씨 터짐 */
  private heatBurst(x: number, z: number, col: number, enrage: boolean, view: GameView): void {
    const P = this.add;
    this.glow.add(GlowKind.Shock, x, z, 0, 2, 16, 2, 16, 0.9, col, 1.8, 0.9);
    this.glow.add(GlowKind.Shock, x, z, 0, 2, 10, 2, 10, 1.2, 0xffd080, 1.4, 0.7);
    for (let c = 0; c < P.count(46, 12); c++) {
      const a = P.range(0, Math.PI * 2), r = P.range(1.5, 4), s = P.range(2, 7);
      P.emit(AddKind.Ember, Orient.Camera, x + Math.sin(a) * r, P.range(0.5, 4), z + Math.cos(a) * r, Math.sin(a) * s, P.range(3, 8), Math.cos(a) * s, P.range(1, 1.8), P.range(0.18, 0.3), 0.04, c % 3 ? col : EMBER_HOT, 2.1, 0.95, -1, 1.2);
    }
    if (enrage && this.enrageId < 0 && view.boss.state !== BossState.Dying) this.enrageId = this.glow.add(GlowKind.Disc, x, z, 0, 5, 5, 5, 5, 1e6, 0xff2a10, 1.2, 0.35);
    this.punch(4, 140, x, z, view);
  }

  /**
   * 포효(등장·2페이즈): 입에서 공중 음파 고리 셋(0.12초 간격처럼 크기·수명을 달리) + 바닥을 쓸고 나가는 큰 충격 고리 둘 + 흙먼지 물결 + 바깥으로 튀는 불씨.
   * 화면 전체 섬광 없음, 단발 펀치 6px(AV-SPEC 카메라 헌법: 줌 펄스·지속 흔들림 없음).
   */
  private roarBlast(k: number, view: GameView): void {
    const P = this.add, A = this.alpha, ctx = this.ctx;
    const x = ctx.bossX, z = ctx.bossZ;
    this.mouthWorld(this.lastBody, this.mouth);
    const my = Math.max(1.5, this.mouth.y);
    // 공중 음파는 입 앞에서 작게(얼굴을 덮지 않게, L11 비평 2회차) — 주황 고리 둘, 크기 3.5m까지
    for (let c = 0; c < 2; c++) P.emit(AddKind.Ring, Orient.Camera, this.mouth.x, my, this.mouth.z, 0, 0, 0, 0.45 + c * 0.15, 0.6, (2.6 + c * 0.9) * k, 0xff9a40, 1.4 - c * 0.3, 0.5 - c * 0.15);
    // 바닥 충격 고리: 흰 원판 대신 얇은 주황 고리(식으며 붉게)
    this.glow.add(GlowKind.Shock, x, z, 0, 3, 19 * k, 3, 19 * k, 1.0, 0xff8a3a, 1.3, 0.6);
    this.glow.add(GlowKind.Shock, x, z, 0, 2, 12 * k, 2, 12 * k, 0.7, 0xffb060, 1.2, 0.45);
    this.dark.add(DarkKind.DustRing, x, z, 0, 3, 17 * k, 3, 17 * k, 1.3, 0x7a6a58, 1, 0.6);
    for (let c = 0; c < A.count(28, 8); c++) {
      const a = A.range(0, Math.PI * 2), r = A.range(3, 5), s = A.range(8, 13) * k;
      A.emit(AlphaKind.Dust, Orient.Camera, x + Math.sin(a) * r, 0.4, z + Math.cos(a) * r, Math.sin(a) * s, A.range(0.2, 1.2), Math.cos(a) * s, A.range(0.8, 1.3), 1.2, 3.6, DUST, 1, 0.5, 0.5, 1.6);
    }
    for (let c = 0; c < P.count(30, 8); c++) {
      const a = P.range(0, Math.PI * 2), s = P.range(6, 12) * k;
      P.emit(AddKind.Ember, Orient.Camera, this.mouth.x, my, this.mouth.z, Math.sin(a) * s, P.range(1, 5), Math.cos(a) * s, P.range(0.6, 1.1), P.range(0.14, 0.24), 0.04, c % 2 ? EMBER_HOT : EMBER, 2.0, 0.9, -1, 1.4);
    }
    this.cnt.roars++;
    this.punch(6 * k, 170, x, z, view);
  }

  private defeated(x: number, z: number): void {
    const P = this.add, A = this.alpha;
    for (let c = 0; c < P.count(40, 10); c++) {
      const a = P.range(0, Math.PI * 2), r = P.range(0.5, 3);
      P.emit(AddKind.Ember, Orient.Camera, x + Math.sin(a) * r, P.range(0.5, 3), z + Math.cos(a) * r, Math.sin(a) * 1.5, P.range(2, 6), Math.cos(a) * 1.5, P.range(1.2, 2.2), 0.24, 0.05, EMBER, 2.0, 0.9, 0.2, 0.8);
    }
    for (let c = 0; c < A.count(8, 3); c++) {
      A.emit(AlphaKind.Smoke, Orient.Camera, x + A.range(-2, 2), 1.5, z + A.range(-2, 2), A.range(-0.5, 0.5), A.range(1, 2.2), A.range(-0.5, 0.5), A.range(2, 3), 2, 5, SMOKE, 1, 0.5, 0.1, 0.5);
    }
    if (this.enrageId >= 0) { this.glow.end(this.enrageId, 0.6); this.enrageId = -1; }
  }

  /** 상태로 읽는 연출: 등장 착지·이륙 바람·비행 아래 바람·돌진 흙먼지 */
  private statePolls(view: GameView, dt: number, body: BossBody | null): void {
    const b = view.boss, r = view.raid!, ctx = this.ctx;
    const st = b.state, tick = b.stateTick;
    if (st === BossState.Intro && this.lastState === BossState.Intro && this.lastStateTick < INTRO_LAND && tick >= INTRO_LAND) this.landImpact(ctx.bossX, ctx.bossZ, 0.65, view);
    if (st === BossState.Intro && this.lastState === BossState.Intro && this.lastStateTick < INTRO_ROAR && tick >= INTRO_ROAR) this.roarBlast(1, view);
    this.lastState = st; this.lastStateTick = tick;
    if (dt <= 0) return;
    const air = r.air;
    const A = this.alpha, P = this.add;
    // 이륙(무적 3초): 날갯짓마다 흙먼지 고리, 바깥으로 도는 흙먼지·위로 솟는 불씨. 높이 오를수록 약해진다
    const takeoff = r.pattern === WyvernPattern.takeoff && air > 0 && air < 1;
    const landing = r.pattern === WyvernPattern.land && air > 0;
    const lowAir = takeoff || landing || (air > 0 && air < 1);
    if (lowAir || (air >= 1 && body)) {
      const k = takeoff ? 1 - air * 0.7 : landing ? 1 - air * 0.8 : 0.25;
      this.accA += dt * 2.2 * (takeoff || landing ? 1 : 0.4);
      if (this.accA >= 1) {
        this.accA -= 1;
        this.dark.add(DarkKind.DustRing, ctx.bossX, ctx.bossZ, 0, 2, 9 * k + 2, 2, 9 * k + 2, 1.0, 0x7a6a58, 1, 0.55 * k);
      }
      this.accB += dt * 26 * k * A.scale;
      while (this.accB >= 1) {
        this.accB -= 1;
        const a = A.range(0, Math.PI * 2), rr = A.range(2, 5), s = A.range(3, 7) * k;
        // 소용돌이: 바깥 + 접선
        A.emit(AlphaKind.Dust, Orient.Camera, ctx.bossX + Math.sin(a) * rr, 0.4, ctx.bossZ + Math.cos(a) * rr, Math.sin(a) * s + Math.cos(a) * s * 0.8, A.range(0.3, 1.5), Math.cos(a) * s - Math.sin(a) * s * 0.8,
          A.range(0.9, 1.5), 1.0, 3.2, DUST, 1, 0.5 * k, 0.4, 1.4);
      }
      if (takeoff) {
        this.accC += dt * 20 * P.scale;
        while (this.accC >= 1) {
          this.accC -= 1;
          const a = P.range(0, Math.PI * 2), rr = P.range(1, 4);
          P.emit(AddKind.Ember, Orient.Camera, ctx.bossX + Math.sin(a) * rr, 0.3, ctx.bossZ + Math.cos(a) * rr, Math.cos(a) * 3, P.range(4, 9), -Math.sin(a) * 3, P.range(0.9, 1.5), 0.2, 0.04, EMBER, 2.0, 0.9, -1, 0.6);
        }
      }
    }
    // 돌진: 내달리는 동안 발밑 흙먼지와 뒤로 튀는 불똥
    if (this.chargeLeft > 0) {
      this.chargeLeft -= dt;
      if (st === BossState.Slam || st === BossState.Chase) {
        this.accD += dt * 30 * A.scale;
        while (this.accD >= 1) {
          this.accD -= 1;
          const bx = ctx.bossX - Math.sin(this.yaw) * 1.5 + A.range(-1.5, 1.5), bz = ctx.bossZ - Math.cos(this.yaw) * 1.5 + A.range(-1.5, 1.5);
          A.emit(AlphaKind.Dust, Orient.Camera, bx, 0.4, bz, -Math.sin(this.yaw) * 3 + A.range(-1, 1), A.range(0.5, 1.5), -Math.cos(this.yaw) * 3 + A.range(-1, 1), A.range(0.7, 1.1), 0.9, 2.6, DUST, 1, 0.55, 0.3, 1.6);
          if (P.rnd() < 0.4) P.emit(AddKind.Streak, Orient.Velocity, bx, 0.3, bz, -Math.sin(this.yaw) * 8 + P.range(-2, 2), P.range(2, 5), -Math.cos(this.yaw) * 8 + P.range(-2, 2), 0.3, 0.07, 0.03, EMBER_HOT, 2.1, 0.9, -12, 1, 0.05);
        }
      }
    }
  }

  /** 보스 둘레 불씨·열기: 1페이즈 약하게, 2페이즈 더, 광폭은 더 많이 + 붉은 바닥 맥동 */
  private ambient(view: GameView, dt: number): void {
    const r = view.raid!, ctx = this.ctx, P = this.add;
    // 격노 오라 수명 = 격노이고 살아 있는 동안(쓰러지기 시작하면 끝낸다 — Codex P2 #217)
    if (this.enrageId >= 0 && (!r.enraged || view.boss.state === BossState.Dying || view.boss.hp <= 0)) { this.glow.end(this.enrageId, 0.6); this.enrageId = -1; }
    if (this.enrageId >= 0) {
      if (!this.glow.move(this.enrageId, ctx.bossX, ctx.bossZ)) this.enrageId = -1;
      const pulse = 0.75 + 0.25 * Math.sin(ctx.time * 3.2);
      this.marks.set(MARK_ENRAGE, AddKind.Heat, Orient.Upright, ctx.bossX, ctx.bossMidY, ctx.bossZ, 4.5, 1.2, 0xff5a2a, 0.8, 0.35 * pulse);
    } else if (r.enraged && view.boss.state !== BossState.Dying && view.boss.hp > 0) {
      this.enrageId = this.glow.add(GlowKind.Disc, ctx.bossX, ctx.bossZ, 0, 5, 5, 5, 5, 1e6, 0xff2a10, 1.2, 0.35);
    }
    const hard = r.diff === 'hard';
    // 하드: 광폭 전에도 보스 둘레 열기 일렁임을 옅게(광폭 열기 칸을 같이 쓴다 — 광폭이면 위에서 이미 더 진하게 놓았다)
    if (hard && this.enrageId < 0) this.marks.set(MARK_ENRAGE, AddKind.Heat, Orient.Upright, ctx.bossX, ctx.bossMidY, ctx.bossZ, 4.2, 1.2, HEAT_HARD, 0.7, 0.2 * (0.8 + 0.2 * Math.sin(ctx.time * 2.6)));
    if (dt <= 0) return;
    // 살아 있는 불 장판: 불씨가 솟고 용암 거품이 터진다(식어 가면 줄어든다)
    for (let i = 0; i < r.fires.length; i++) {
      const f = r.fires[i];
      if (!f.active) continue;
      const hot = Math.min(1, f.ttl / 90);
      if (P.rnd() < dt * 7 * hot * P.scale) {
        const a = P.range(0, Math.PI * 2), rr = f.radius * Math.sqrt(P.rnd()) * 0.9;
        P.emit(AddKind.Ember, Orient.Camera, f.x + Math.sin(a) * rr, 0.15, f.z + Math.cos(a) * rr, P.range(-0.3, 0.3), P.range(1.2, 3), P.range(-0.3, 0.3), P.range(0.8, 1.4), P.range(0.12, 0.2), 0.03, P.rnd() < 0.4 ? EMBER_HOT : EMBER, 1.9, 0.9, 0.4, 0.4);
      }
      if (P.rnd() < dt * 2.2 * hot * P.scale) {
        const a = P.range(0, Math.PI * 2), rr = f.radius * Math.sqrt(P.rnd()) * 0.8;
        const x = f.x + Math.sin(a) * rr, z = f.z + Math.cos(a) * rr;
        P.emit(AddKind.Ring, Orient.Camera, x, 0.15, z, 0, 0.2, 0, 0.3, 0.15, 0.55, EMBER_HOT, 2.0, 0.8);
        for (let c = 0; c < 3; c++) P.emit(AddKind.Streak, Orient.Velocity, x, 0.15, z, P.range(-1.5, 1.5), P.range(2, 4), P.range(-1.5, 1.5), 0.35, 0.05, 0.02, EMBER_HOT, 2.1, 0.9, -10, 0, 0.06);
      }
    }
    this.arenaAir(dt, r.phase === 2);
    const rate = (r.enraged ? 18 : r.phase === 2 ? 10 : 3.5) * (hard ? 1.25 : 1);
    this.accC += dt * rate * P.scale;
    while (this.accC >= 1) {
      this.accC -= 1;
      const a = P.range(0, Math.PI * 2), rr = P.range(2, 7);
      P.emit(AddKind.Ember, Orient.Camera, ctx.bossX + Math.sin(a) * rr, P.range(0.2, 2), ctx.bossZ + Math.cos(a) * rr, Math.cos(a) * 0.8, P.range(1.2, 3), -Math.sin(a) * 0.8, P.range(1.2, 2.2), P.range(0.12, 0.2), 0.03, r.enraged ? 0xff4a20 : hard ? EMBER_HARD : EMBER, 1.9, 0.85, 0.3, 0.4);
    }
  }

  /**
   * 둥지 공기(L11): 섬 둘레 용암 바다에서 바람에 실려 솟는 불티(초당 16, 2페이즈 24)와 가장자리 열기 일렁임(초당 0.8).
   * 섬 = RAID_MAPS.raid_wyvern 아레나 원(중심 0, -46 · 반지름 19).
   */
  private arenaAir(dt: number, phase2: boolean): void {
    const P = this.add;
    this.accAir += dt * (phase2 ? 24 : 16) * P.scale;
    while (this.accAir >= 1) {
      this.accAir -= 1;
      const a = P.range(0, Math.PI * 2), rr = P.range(19.5, 28);
      P.emit(AddKind.Ember, Orient.Camera, ARENA_X + Math.sin(a) * rr, P.range(-1, 1), ARENA_Z + Math.cos(a) * rr, 0.9 + P.range(-0.4, 0.4), P.range(1.5, 3.4), -0.5 + P.range(-0.4, 0.4),
        P.range(2.2, 3.6), P.range(0.1, 0.18), 0.03, P.rnd() < 0.3 ? EMBER_HOT : EMBER, 1.8, 0.8, 0.15, 0.5);
    }
    if (P.rnd() < dt * 0.8 * P.scale) {
      const a = P.range(0, Math.PI * 2), rr = P.range(19.5, 22);
      P.emit(AddKind.Heat, Orient.Upright, ARENA_X + Math.sin(a) * rr, 1.2, ARENA_Z + Math.cos(a) * rr, 0, 0.5, 0, P.range(1.6, 2.4), 2.2, 3.2, 0xff9a50, 0.5, 0.3, 0, 0, 0, 1.6);
    }
  }

  prewarm(on: boolean): void {
    this.group.visible = on;
    this.add.prewarm(on); this.alpha.prewarm(on); this.glow.prewarm(on); this.dark.prewarm(on); this.debris.prewarm(on); this.marks.prewarm(on);
  }

  clear(): void {
    this.add.clear(); this.alpha.clear(); this.glow.clear(); this.dark.clear(); this.debris.clear(); this.marks.clear(); this.proj.clear();
    this.brand.clear();
    for (const p of this.party) p.clear();
    this.tShape.fill(-1); this.tWas.fill(0); this.tSweep.fill(-1); this.tSpit.fill(0);
    this.enrageId = -1; this.chargeLeft = 0; this.hasYaw = false; this.lastState = -1;
    this.accA = this.accB = this.accC = this.accD = 0;
  }

  dispose(): void {
    this.add.dispose(); this.alpha.dispose(); this.glow.dispose(); this.dark.dispose(); this.debris.dispose(); this.marks.dispose();
  }
}

function angleDelta(from: number, to: number): number {
  let d = to - from;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}
function easeInOut(t: number): number { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
